import React, { useState, useCallback, useMemo } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import schedulesModalImg from '@/assets/images/modals/modal-schedules.png'
import { Schedule } from '@/core/api/entities/schedule'
import permissionService, {
	PERMISSIONS,
} from '@/core/api/services/permissionService'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import {
	useAssignedSchedules,
	useInUseSchedules,
	useFavoriteSchedules,
	useSchedules,
	useUpdateSchedule,
	useToggleScheduleFavorite,
} from '@/core/hooks/useSchedule'
import { useDebounce } from '@/core/hooks/utils/useDebounce'
import { useTranslation } from '@/core/i18n/useTranslation'
import SuccessModal from '@/shared/components/molecules/SuccessModal/SuccessModal'
import { useNotificationContext } from '@/shared/context/NotificationProvider'
import { useWelcomeModal } from '@/shared/hooks/useWelcomeModal'

import { useFilteredScheduleSections } from './hooks/useFilteredScheduleSections'
import SchedulesListPageUI from './SchedulesListPageUI'

const SchedulesListPage: React.FC = () => {
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const { showNotification } = useNotificationContext()
	const { t: tCommon } = useTranslation('common')

	// Welcome modal with localStorage persistence and URL parameter support
	const {
		showModal: showWelcomeModal,
		handleCloseModal: handleCloseWelcomeModal,
	} = useWelcomeModal({
		modalKey: 'schedules',
	})

	// Search state
	const [searchQuery, setSearchQuery] = useState('')
	const debouncedSearchQuery = useDebounce(searchQuery, 400)

	// Create/Edit modal state
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
	const [scheduleName, setScheduleName] = useState('')
	const [scheduleDescription, setScheduleDescription] = useState('')
	const [editingScheduleId, setEditingScheduleId] = useState<string | null>(
		null
	)
	const [isSubmittingSchedule, setIsSubmittingSchedule] = useState(false)

	// Assign modal state
	const [isAssignModalOpen, setIsAssignModalOpen] = useState(false)
	const [selectedScheduleForAssign, setSelectedScheduleForAssign] = useState<{
		id: string
		name: string
	} | null>(null)

	// Remove modal state
	const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false)
	const [selectedScheduleForRemove, setSelectedScheduleForRemove] = useState<{
		id: string
		name: string
		description?: string
	} | null>(null)

	// Duplicate modal state
	const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)
	const [selectedScheduleForDuplicate, setSelectedScheduleForDuplicate] =
		useState<{
			id: string
			name: string
			description?: string
		} | null>(null)

	// Pagination state for each carousel
	const [inUsePage, setInUsePage] = useState(1)
	const [assignedPage, setAssignedPage] = useState(1)
	const [favoritesPage, setFavoritesPage] = useState(1)

	// Track loading state for favorite toggle
	const [loadingScheduleId, setLoadingScheduleId] = useState<number | null>(
		null
	)

	// Memoize params for stable query keys
	const inUseSchedulesParams = useMemo(
		() => ({
			per_page: 5,
			page: inUsePage,
		}),
		[inUsePage]
	)

	const assignedSchedulesParams = useMemo(
		() => ({
			per_page: 5,
			page: assignedPage,
			public: false,
		}),
		[assignedPage]
	)

	const favoriteSchedulesParams = useMemo(
		() => ({
			per_page: 5,
			page: favoritesPage,
		}),
		[favoritesPage]
	)

	const searchSchedulesParams = useMemo(
		() => ({
			per_page: 10,
			query: debouncedSearchQuery,
		}),
		[debouncedSearchQuery]
	)

	// Fetch in-use schedules (Assigned schedules)
	const {
		data: inUseSchedulesData,
		isLoading: isLoadingInUse,
		refetch: refetchInUse,
	} = useInUseSchedules({
		config: useAccountConfig({
			params: {
				...inUseSchedulesParams,
			},
		}),
	})

	// Fetch assigned schedules (Custom schedules)
	const {
		data: assignedSchedulesData,
		isLoading: isLoadingAssigned,
		refetch: refetchAssigned,
	} = useAssignedSchedules({
		config: useAccountConfig({
			params: {
				...assignedSchedulesParams,
			},
		}),
	})

	// Fetch favorite schedules
	const {
		data: favoriteSchedulesData,
		isLoading: isLoadingFavorites,
		refetch: refetchFavorites,
	} = useFavoriteSchedules({
		config: useAccountConfig({
			params: {
				...favoriteSchedulesParams,
			},
		}),
	})

	// Fetch schedules for search
	const { data: searchSchedulesData, isLoading: isLoadingSearch } =
		useSchedules({
			config: useAccountConfig({
				params: searchSchedulesParams,
			}),
			options: {
				enabled: debouncedSearchQuery.length > 0,
			},
		})

	// Progressive loading for business category sections
	const { visibleSections, hasMore, loadMore } = useFilteredScheduleSections()

	// Update schedule mutation
	const { mutateAsync: updateSchedule } = useUpdateSchedule({
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ['schedules-assigned'] })
				queryClient.invalidateQueries({ queryKey: ['schedules-in-use'] })
			},
		},
	})

	// Toggle favorite mutation
	const toggleFavoriteMutation = useToggleScheduleFavorite({
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				setLoadingScheduleId(null)
				refetchFavorites()
				refetchAssigned()
				refetchInUse()
				showNotification({
					message: 'Favorites updated',
					type: 'success',
				})
			},
			onError: () => {
				setLoadingScheduleId(null)
				showNotification({
					message: 'Failed to update favorites',
					type: 'error',
				})
			},
		},
	})

	// Get assigned and favorite schedules
	const inUseSchedules = inUseSchedulesData?.data || []
	const assignedSchedules = assignedSchedulesData?.data || []
	const favoriteSchedules = favoriteSchedulesData?.data || []

	// Create a Set of favorite schedule IDs for quick lookup
	const favoriteScheduleIds = useMemo(() => {
		return new Set(favoriteSchedules.map((schedule) => schedule.id))
	}, [favoriteSchedules])

	const searchResults = searchSchedulesData?.data || []
	const canCreateSchedules = permissionService.currentUserHasAnyPermission([
		PERMISSIONS.CREATE_SCHEDULES,
		PERMISSIONS.MANAGE_CHILDREN_SCHEDULES,
		PERMISSIONS.MANAGE_OWN_LEVEL_SCHEDULES,
	])
	const canEditSchedule = permissionService.currentUserHasAnyPermission([
		PERMISSIONS.MANAGE_CHILDREN_SCHEDULES,
		PERMISSIONS.MANAGE_OWN_LEVEL_SCHEDULES,
		PERMISSIONS.UPDATE_SCHEDULES,
	])
	const canDeleteSchedule = permissionService.currentUserHasAnyPermission([
		PERMISSIONS.MANAGE_CHILDREN_SCHEDULES,
		PERMISSIONS.MANAGE_OWN_LEVEL_SCHEDULES,
		PERMISSIONS.DESTROY_SCHEDULES,
	])

	// Determine if search is active
	const showSearchResults = debouncedSearchQuery.length > 0

	// Pagination metadata
	const inUsePagination = inUseSchedulesData?.meta?.pagination
	const assignedPagination = assignedSchedulesData?.meta?.pagination
	const favoritesPagination = favoriteSchedulesData?.meta?.pagination

	// Handle favorite click
	const handleFavoriteClick = useCallback(
		(schedule: Schedule, isFavoritesCarousel: boolean) => {
			setLoadingScheduleId(schedule.id)

			let favorite: boolean
			if (isFavoritesCarousel) {
				// In favorites carousel, always unfavorite
				favorite = false
			} else {
				// In other carousels, toggle based on current status
				const isCurrentlyFavorite = favoriteScheduleIds.has(schedule.id)
				favorite = !isCurrentlyFavorite
			}

			toggleFavoriteMutation.mutate({
				scheduleId: String(schedule.id),
				favorite,
			})
		},
		[toggleFavoriteMutation, favoriteScheduleIds]
	)

	// Pagination handlers with wrap-around
	const handleInUsePageChange = useCallback(
		(direction: 'next' | 'prev') => {
			setInUsePage((prev) => {
				if (!inUsePagination || inUsePagination.total_pages <= 1) {
					return prev
				}
				if (direction === 'next') {
					if (prev >= inUsePagination.total_pages) {
						return 1
					}
					return prev + 1
				} else {
					if (prev <= 1) {
						return inUsePagination.total_pages
					}
					return prev - 1
				}
			})
		},
		[inUsePagination]
	)

	const handleAssignedPageChange = useCallback(
		(direction: 'next' | 'prev') => {
			setAssignedPage((prev) => {
				if (!assignedPagination || assignedPagination.total_pages <= 1) {
					return prev
				}
				if (direction === 'next') {
					if (prev >= assignedPagination.total_pages) {
						return 1
					}
					return prev + 1
				} else {
					if (prev <= 1) {
						return assignedPagination.total_pages
					}
					return prev - 1
				}
			})
		},
		[assignedPagination]
	)

	const handleFavoritesPageChange = useCallback(
		(direction: 'next' | 'prev') => {
			setFavoritesPage((prev) => {
				if (!favoritesPagination || favoritesPagination.total_pages <= 1) {
					return prev
				}
				if (direction === 'next') {
					if (prev >= favoritesPagination.total_pages) {
						return 1
					}
					return prev + 1
				} else {
					if (prev <= 1) {
						return favoritesPagination.total_pages
					}
					return prev - 1
				}
			})
		},
		[favoritesPagination]
	)

	// Schedule click handler
	const handleScheduleClick = useCallback(
		(schedule: Schedule) => {
			navigate(`/schedules/${schedule.id}`)
		},
		[navigate]
	)

	// Create schedule handler
	const handleCreateSchedule = useCallback(async () => {
		if (!scheduleName.trim()) return

		setIsSubmittingSchedule(true)

		try {
			if (editingScheduleId) {
				await updateSchedule({
					scheduleId: editingScheduleId,
					data: {
						title: {
							'en-US': scheduleName.trim(),
						},
						description: {
							'en-US': scheduleDescription.trim() || '',
						},
						timeslots: [],
					},
				})

				showNotification({
					type: 'success',
					message: 'Schedule updated',
					description: `"${scheduleName}" has been successfully updated.`,
				})

				setEditingScheduleId(null)
			} else {
				const params = new URLSearchParams({
					name: scheduleName.trim(),
					description: scheduleDescription.trim() || '',
					mode: 'create',
				})

				navigate(`/schedules/new?${params.toString()}`)
			}

			setIsCreateModalOpen(false)
			setScheduleName('')
			setScheduleDescription('')
		} catch {
			showNotification({
				type: 'error',
				message: editingScheduleId
					? 'Failed to update schedule'
					: 'Failed to create schedule',
				description: 'Please try again.',
			})
		} finally {
			setIsSubmittingSchedule(false)
		}
	}, [
		scheduleName,
		scheduleDescription,
		editingScheduleId,
		updateSchedule,
		navigate,
		showNotification,
	])

	// Cancel create handler
	const handleCancelCreate = useCallback(() => {
		setIsCreateModalOpen(false)
		setScheduleName('')
		setScheduleDescription('')
		setEditingScheduleId(null)
	}, [])

	// Modal handlers
	const handleOpenCreateModal = useCallback(() => {
		setIsCreateModalOpen(true)
	}, [])

	const handleAssign = useCallback((scheduleId: number, name: string) => {
		setSelectedScheduleForAssign({
			id: String(scheduleId),
			name,
		})
		setIsAssignModalOpen(true)
	}, [])

	const handleDuplicate = useCallback(
		(scheduleId: number, name: string, description: string) => {
			if (!canCreateSchedules) {
				return
			}

			setSelectedScheduleForDuplicate({
				id: String(scheduleId),
				name,
				description,
			})
			setIsDuplicateModalOpen(true)
		},
		[canCreateSchedules]
	)

	const handleEdit = useCallback(
		(scheduleId: number) => {
			navigate(`/schedules/${scheduleId}?mode=edit`)
		},
		[navigate]
	)

	const handleRemove = useCallback(
		(scheduleId: number, name: string, description: string) => {
			setSelectedScheduleForRemove({
				id: String(scheduleId),
				name,
				description,
			})
			setIsRemoveModalOpen(true)
		},
		[]
	)

	// Check if initial data is loading
	const isInitialLoading =
		isLoadingAssigned || isLoadingFavorites || isLoadingInUse

	return (
		<>
			<SchedulesListPageUI
				// Loading states
				isInitialLoading={isInitialLoading}
				isLoadingInUse={isLoadingInUse}
				isLoadingAssigned={isLoadingAssigned}
				isLoadingFavorites={isLoadingFavorites}
				// Search
				searchQuery={searchQuery}
				onSearchChange={setSearchQuery}
				showSearchResults={showSearchResults}
				isLoadingSearch={isLoadingSearch}
				searchResults={searchResults}
				// In-use schedules
				inUseSchedules={inUseSchedules}
				inUsePagination={inUsePagination}
				onInUsePageChange={handleInUsePageChange}
				// Assigned schedules
				assignedSchedules={assignedSchedules}
				assignedPagination={assignedPagination}
				onAssignedPageChange={handleAssignedPageChange}
				// Favorite schedules
				favoriteSchedules={favoriteSchedules}
				favoritesPagination={favoritesPagination}
				onFavoritesPageChange={handleFavoritesPageChange}
				favoriteScheduleIds={favoriteScheduleIds}
				// Filtered sections (business categories)
				filteredSections={visibleSections}
				hasMoreFilteredSections={hasMore}
				onLoadMoreFilteredSections={loadMore}
				// Schedule actions
				canCreateSchedule={canCreateSchedules}
				onScheduleClick={handleScheduleClick}
				onFavoriteClick={handleFavoriteClick}
				loadingScheduleId={loadingScheduleId}
				onAssign={handleAssign}
				onDuplicate={canCreateSchedules ? handleDuplicate : undefined}
				onEdit={canEditSchedule ? handleEdit : undefined}
				onRemove={canDeleteSchedule ? handleRemove : undefined}
				// Create modal
				isCreateModalOpen={isCreateModalOpen}
				onOpenCreateModal={handleOpenCreateModal}
				onCloseCreateModal={() => setIsCreateModalOpen(false)}
				scheduleName={scheduleName}
				onScheduleNameChange={setScheduleName}
				scheduleDescription={scheduleDescription}
				onScheduleDescriptionChange={setScheduleDescription}
				editingScheduleId={editingScheduleId}
				isSubmittingSchedule={isSubmittingSchedule}
				onCreateSchedule={handleCreateSchedule}
				onCancelCreate={handleCancelCreate}
				// Assign modal
				isAssignModalOpen={isAssignModalOpen}
				onAssignModalChange={setIsAssignModalOpen}
				selectedScheduleForAssign={selectedScheduleForAssign}
				// Remove modal
				isRemoveModalOpen={isRemoveModalOpen}
				onRemoveModalChange={setIsRemoveModalOpen}
				selectedScheduleForRemove={selectedScheduleForRemove}
				// Duplicate modal
				isDuplicateModalOpen={isDuplicateModalOpen}
				onDuplicateModalChange={setIsDuplicateModalOpen}
				selectedScheduleForDuplicate={selectedScheduleForDuplicate}
			/>

			<SuccessModal
				visible={showWelcomeModal}
				image={schedulesModalImg}
				imageAlt={tCommon('welcomeModals.schedules.title')}
				title={tCommon('welcomeModals.schedules.title')}
				description={tCommon('welcomeModals.schedules.description')}
				primaryAction={{
					label: tCommon('welcomeModals.schedules.continue'),
					onClick: handleCloseWelcomeModal,
				}}
				onClose={handleCloseWelcomeModal}
			/>
		</>
	)
}

export default SchedulesListPage
